import { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Button, Checkbox, Select, TextInput } from "@mantine/core";
import {
  useUpdatePrintifyProductDetailsMutation,
  useUpdatePrintifyPublicationMutation,
} from "../../features/api/ProductApiSlice";
import "./printifyProductEditor.scss";

const CATEGORY_OPTIONS = [
  "Small Car Essentials",
  "Sleep & Comfort",
  "Filming the Journey",
  "Safety & Tech",
  "Apparel",
  "Merchandise",
];

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const initialForm = (product) => ({
  name: product.name || "",
  description: product.description || "",
  category: product.category || "Merchandise",
  tags: (product.tags || []).join(", "),
  featured: Boolean(product.featured),
  variantPrices: Object.fromEntries(
    (product.variants || []).map((variant) => [
      String(variant._id),
      String(variant.price ?? ""),
    ])
  ),
});

const variantLabel = (variant) => {
  const options = variant.optionValues || {};
  const entries = Object.entries(options);
  if (entries.length) {
    return entries.map(([key, value]) => `${key}: ${value}`).join(" · ");
  }
  return (
    [variant.size, variant.colorName, variant.volume]
      .filter(Boolean)
      .join(" · ") ||
    variant.sku ||
    "Variant"
  );
};

export default function PrintifyProductEditor({ product, onClose }) {
  const [form, setForm] = useState(() => initialForm(product));
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [priceFilter, setPriceFilter] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [updateDetails, { isLoading: isSaving }] =
    useUpdatePrintifyProductDetailsMutation();
  const [updatePublication, { isLoading: isPublishing }] =
    useUpdatePrintifyPublicationMutation();

  useEffect(() => {
    setForm(initialForm(product));
    setError("");
    setSuccess("");
    setHasUnsavedChanges(false);
  }, [product._id]);

  const variants = useMemo(() => product.variants || [], [product.variants]);
  const availableCount = variants.filter((v) => v.isAvailable === true).length;
  const isBusy = isSaving || isPublishing;
  const visibleVariants = variants.filter((variant) => {
    if (!showUnavailable && variant.isAvailable !== true) return false;
    return variantLabel(variant)
      .toLowerCase()
      .includes(priceFilter.toLowerCase());
  });

  const change = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setHasUnsavedChanges(true);
    setSuccess("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!form.name.trim()) return setError("Enter a product name.");
    if (!form.description.replace(/<[^>]*>/g, "").trim()) {
      return setError("Enter a product description.");
    }
    if (!form.category) return setError("Select a category.");

    // Submit all variants, including unavailable ones, to preserve their prices.
    const variantPrices = [];
    for (const variant of variants) {
      const raw = String(form.variantPrices[String(variant._id)] ?? "").trim();
      const price = Number(raw);
      if (
        !raw ||
        !Number.isFinite(price) ||
        price <= 0 ||
        Math.abs(price * 100 - Math.round(price * 100)) > 0.000001
      ) {
        setShowUnavailable(true);
        return setError(`Check the price for ${variantLabel(variant)}.`);
      }
      variantPrices.push({
        variantId: variant._id,
        price: Math.round(price * 100) / 100,
      });
    }

    try {
      const response = await updateDetails({
        id: product._id,
        data: {
          name: form.name.trim(),
          description: form.description,
          category: form.category,
          tags: form.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
          featured: form.featured,
          variantPrices,
        },
      }).unwrap();
      setSuccess(response.message || "Product details saved.");
      setHasUnsavedChanges(false);
    } catch (err) {
      setError(
        err?.data?.message || "Unable to save Printify product details."
      );
    }
  };

  const handlePublication = async () => {
    if (hasUnsavedChanges) {
      setError("Save your product changes before changing publication status.");
      return;
    }
    const nextStatus = !product.isPublished;
    if (
      !window.confirm(
        `Do you want to ${nextStatus ? "publish" : "unpublish"} “${
          product.name
        }” on Mini Miles?`
      )
    )
      return;
    setError("");
    setSuccess("");
    try {
      const response = await updatePublication({
        id: product._id,
        isPublished: nextStatus,
      }).unwrap();
      setSuccess(response.message || "Publication status updated.");
    } catch (err) {
      setError(err?.data?.message || "Unable to update publication status.");
    }
  };

  const closeEditor = () => {
    if (hasUnsavedChanges && !window.confirm("Discard unsaved changes?"))
      return;
    onClose();
  };

  return (
    <div className="printify-product-editor">
      <header className="printify-editor-header">
        <div>
          <span className="printify-eyebrow">PRINTIFY · PRODUCT EDITOR</span>
          <h2>Manage product</h2>
          <p>
            Customize your Mini Miles listing. Fulfillment options remain synced
            from Printify.
          </p>
        </div>
        <button
          type="button"
          className="printify-close-btn"
          onClick={closeEditor}
          disabled={isBusy}
        >
          ✕ Close
        </button>
      </header>

      <div className="printify-editor-summary">
        <img
          src={
            product.images?.[0] ||
            product.variants?.find((v) => v.colorImages?.length)
              ?.colorImages?.[0]
          }
          alt=""
        />

        <div className="printify-editor-summary-text">
          <strong>{product.name}</strong>

          <div className="printify-summary-meta">
            <span
              className={
                product.isPublished ? "status-published" : "status-unpublished"
              }
            >
              ● {product.isPublished ? "Published" : "Unpublished"}
            </span>

            <span>
              {availableCount} available / {variants.length} variants
            </span>
          </div>
        </div>
      </div>

      {/* ==========================================
          PRINTIFY REFERENCE — READ ONLY
          Original Printify product identification.
          These values are managed by synchronization.
      ========================================== */}
      <section className="printify-reference-section">
        <div className="printify-reference-header">
          <h3>🔗 Printify Reference</h3>

          <span className="printify-reference-badge">Read Only</span>
        </div>

        <p className="printify-reference-description">
          This information identifies the original product in Printify. Your
          Mini Miles product name can be different without affecting
          fulfillment.
        </p>

        <div className="printify-reference-grid">
          <div className="printify-reference-field">
            <span>Original Printify Title</span>

            <strong>
              {product.externalProduct?.originalTitle ||
                "Run Printify Sync to retrieve the original title"}
            </strong>
          </div>

          <div className="printify-reference-field">
            <span>Printify Product ID</span>

            <code>{product.externalProduct?.productId || "Not available"}</code>
          </div>

          <div className="printify-reference-field">
            <span>Printify Shop ID</span>

            <code>{product.externalProduct?.shopId || "Not available"}</code>
          </div>

          <div className="printify-reference-field">
            <span>Fulfillment Provider</span>

            <strong>Printify</strong>
          </div>
        </div>

        <div className="printify-reference-note">
          <span>ℹ️</span>

          <p>
            Changes to the website title, description, and selling prices do not
            change the original Printify listing. Orders will be matched using
            Printify product and variant IDs.
          </p>
        </div>
      </section>

      {/* EXISTING ERROR / SUCCESS MESSAGES */}
      {error && (
        <div className="notes-alert notes-alert--error" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="notes-alert notes-alert--success" role="status">
          {success}
        </div>
      )}

      {error && (
        <div className="notes-alert notes-alert--error" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="notes-alert notes-alert--success" role="status">
          {success}
        </div>
      )}

      <form
        id="printify-details-form"
        className="printify-details-form"
        onSubmit={handleSave}
      >
        <section className="printify-form-section">
          <div className="printify-section-heading">
            <h3>Listing details</h3>
            <p>Shown to customers on the Mini Miles storefront.</p>
          </div>
          <TextInput
            label="Product name"
            required
            value={form.name}
            onChange={(e) => change("name", e.target.value)}
          />
          <div className="quill-wrapper-block">
            <label className="quill-label-text">Product description</label>
            <ReactQuill
              theme="snow"
              value={form.description}
              modules={quillModules}
              onChange={(value) => change("description", value)}
            />
          </div>
          <div className="printify-details-grid">
            <Select
              label="Category"
              required
              searchable
              data={CATEGORY_OPTIONS}
              value={form.category}
              onChange={(value) => change("category", value || "")}
              comboboxProps={{ withinPortal: true, zIndex: 3000 }}
            />
            <TextInput
              label="Tags (comma separated)"
              value={form.tags}
              onChange={(e) => change("tags", e.target.value)}
            />
          </div>
          <Checkbox
            label="Featured Store Spotlight"
            checked={form.featured}
            onChange={(e) => change("featured", e.currentTarget.checked)}
          />
        </section>

        <section className="printify-form-section">
          <div className="printify-section-heading">
            <h3>Variant selling prices</h3>
            <p>
              Only prices are editable. Availability, sizes and colors come from
              Printify.
            </p>
          </div>
          <div className="printify-variant-toolbar">
            <TextInput
              placeholder="Filter by size or color…"
              aria-label="Filter variants"
              value={priceFilter}
              onChange={(e) => setPriceFilter(e.target.value)}
            />
            <Checkbox
              label={`Show unavailable (${variants.length - availableCount})`}
              checked={showUnavailable}
              onChange={(e) => setShowUnavailable(e.currentTarget.checked)}
            />
          </div>
          <div
            className="printify-variant-scroll"
            role="region"
            aria-label="Variant price list"
            tabIndex={0}
          >
            {visibleVariants.length ? (
              visibleVariants.map((variant) => (
                <div className="printify-variant-row" key={variant._id}>
                  <div className="printify-variant-description">
                    <strong>{variantLabel(variant)}</strong>
                    <span
                      className={
                        variant.isAvailable
                          ? "status-published"
                          : "status-unavailable"
                      }
                    >
                      {variant.isAvailable ? "Available" : "Unavailable"}
                    </span>
                  </div>
                  <TextInput
                    label="Price ($)"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.variantPrices[String(variant._id)] ?? ""}
                    onChange={(e) => {
                      setForm((current) => ({
                        ...current,
                        variantPrices: {
                          ...current.variantPrices,
                          [String(variant._id)]: e.target.value,
                        },
                      }));
                      setHasUnsavedChanges(true);
                      setSuccess("");
                    }}
                    disabled={isBusy}
                  />
                </div>
              ))
            ) : (
              <p className="printify-empty-variants">No matching variants.</p>
            )}
          </div>
          <p className="printify-variant-hint">
            Showing {visibleVariants.length} of {variants.length} variants.
            Unavailable variants are hidden from this list by default, not
            deleted.
          </p>
        </section>
      </form>

      <div className="printify-action-bar">
        <div className="printify-action-copy">
          <strong>Store publication</strong>
          <span>
            {product.isPublished
              ? "Visible on Mini Miles"
              : "Hidden from the public storefront"}
          </span>
          {hasUnsavedChanges && (
            <span className="printify-unsaved">● Unsaved changes</span>
          )}
        </div>
        <div className="printify-action-buttons">
          <Button
            type="button"
            variant="outline"
            color={product.isPublished ? "red" : "green"}
            loading={isPublishing}
            disabled={
              isBusy ||
              hasUnsavedChanges ||
              (!product.isPublished && availableCount === 0)
            }
            onClick={handlePublication}
          >
            {product.isPublished ? "Unpublish" : "Publish"}
          </Button>
          <Button
            type="submit"
            form="printify-details-form"
            color="yellow"
            loading={isSaving}
            disabled={isBusy}
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
